import React from 'react'
import {Box, BoxProps, Flex, HStack, Skeleton, Text, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {Accordion, AccordionItem, Button, Picture} from '_uiComponents'
import {SizeGuideSection} from '_types'
import SizeGuideTable from './partials/size-table'
import {useIntl} from 'react-intl'

interface SizeGuideTabProps {
    sections: SizeGuideSection[]
}

/**
 * @name SizeGuideTab
 *
 * @component
 *
 * @description
 * Renders the "Size Guide" tab content inside the SizeGuide drawer: an accordion of
 * sections, each showing the corresponding size table(s).
 *
 * @returns {React.FC} - The rendered size guide tab content.
 */
const SizeGuideTab = ({sections}: SizeGuideTabProps) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('SizeGuide', {}) as Record<string, BoxProps>
    // The size guide image is the same across all sections/tables, so take it from the first one available
    const guideImageTable = sections[0]?.tables[0]
    const hasGuideImage = !!(guideImageTable?.image || guideImageTable?.imageDesktop || guideImageTable?.imageTablet)
    return (
        <VStack>
            {hasGuideImage && (
                <Flex {...styles.firstSectionWrapper}>
                    <Box sx={styles.tableImageWrapper}>
                        <Picture
                            imagesm={guideImageTable?.image ?? undefined}
                            imagemd={guideImageTable?.imageTablet ?? undefined}
                            imagelg={guideImageTable?.imageDesktop ?? undefined}
                            alt={guideImageTable?.imageAlt || ''}
                        />
                    </Box>

                    <Text sx={styles.tabDescription}>
                        {formatMessage({
                            id: 'label.sizeguide.description',
                            defaultMessage:
                                'The following tables show approximate measurements. Small variations may occur due to the characteristics of the fabrics, seasonal changes in fits, and particular washing effects. We recommend referring to another Brunello Cucinelli garment for comparisons.',
                        })}
                    </Text>
                </Flex>
            )}
            <Accordion allowMultiple sx={styles.accordionWrapper} gap="spacing.2">
                {sections.map((section) => (
                    <AccordionItem key={section.id} title={section.name} variant="bgGrey">
                        <Box sx={styles.sectionContent}>
                            {section.isOnlyAsset ? (
                                // Content asset rendering (section.guideAsset) is out of scope for now
                                <Text>{section.guideAsset}</Text>
                            ) : (
                                <SizeGuideTable section={section} />
                            )}
                        </Box>
                    </AccordionItem>
                ))}
            </Accordion>
        </VStack>
    )
}

/**
 * @name SizeGuideTabSkeleton
 *
 * @component
 *
 * @description
 * Skeleton variant for SizeGuideTab to be shown while the size guide data is loading.
 *
 * @returns {React.FC} - The rendered skeleton component.
 */
const SizeGuideTabSkeleton: React.FC = () => {
    const styles = useMultiStyleConfig('SizeGuide', {}) as Record<string, BoxProps>

    return (
        <VStack w="100%" data-testid="size-guide-tab-skeleton">
            <Flex {...styles.firstSectionWrapper} w="100%">
                <Box sx={styles.tableImageWrapper}>
                    <Skeleton w="100%" aspectRatio={1} />
                </Box>
                <VStack sx={styles.tabDescription} alignItems="flex-start" gap="spacing.2">
                    <Skeleton h="16px" w="100%" />
                    <Skeleton h="16px" w="100%" />
                    <Skeleton h="16px" w="60%" />
                </VStack>
            </Flex>
            <VStack sx={styles.accordionWrapper} gap="spacing.2">
                {Array.from({length: 3}, (_, index) => (
                    <Skeleton key={`size-guide-section-skeleton-${index}`} h="52px" w="100%" />
                ))}
            </VStack>
        </VStack>
    )
}

export default SizeGuideTab
export {SizeGuideTabSkeleton}
